import { PencilSquareIcon } from '@heroicons/react/24/solid';
import { Alert, Button, Card, CardBody, CardHeader, Divider, Input, Textarea } from '@heroui/react';
import { skipToken } from '@reduxjs/toolkit/query';
import { type FC, useEffect, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { DietarySelector } from '@/app/components/dietary-selector.tsx';
import { EditEmailDialog } from '@/app/components/edit-email-dialog.tsx';
import { PhoneInput } from '@/app/components/phone-input.tsx';
import { staffEmailCopy } from '@/app/helpers/staff-email.helpers.ts';
import { useAuth } from '@/app/hooks/auth-provider';
import { Text, TextLink } from '@/primitives/text.tsx';
import {
  useFetchDietaryPreferencesQuery,
  useFetchDietaryRestrictionsQuery,
} from '@/slices/dietary-api-slice.ts';
import { useFetchEmailExistsQuery } from '@/slices/user-api-slice.ts';
import { useSecurityContext } from '../../../hooks/security-provider.tsx';
import { emailPattern, hasPermission } from '../../../types/constants.ts';
import { type StaffMemberModel, StaffRoleEnum } from '../../../types/staff-member.type.ts';
import { UserTypeEnum } from '../../../types/user-type.ts';

interface StaffFormProps {
  existingStaffMember?: StaffMemberModel;
  name: string;
  children?: React.ReactNode;
}

const StaffFormFields: FC<StaffFormProps> = ({ existingStaffMember, name, children }) => {
  const { currentAccount } = useSecurityContext();
  const { user } = useAuth();
  const [editEmailOpen, setEmailOpen] = useState(false);

  const { register, watch, control, getValues, trigger } = useFormContext();

  const emailWatch = watch(`${name}.emailAddress`);
  const { data: emailTaken } = useFetchEmailExistsQuery(
    emailWatch && emailWatch.match(emailPattern) ? emailWatch : skipToken,
  );
  const emailConflictMessage = emailTaken?.exists
    ? (emailTaken.message ?? staffEmailCopy.emailAlreadyUsed)
    : undefined;
  const showExistingStaffLink =
    !existingStaffMember &&
    !!emailTaken?.exists &&
    emailTaken.userType === UserTypeEnum.StaffMember &&
    !!emailTaken.userKey &&
    user?.userType === UserTypeEnum.Admin;

  useEffect(() => {
    if (emailWatch && emailWatch.match(emailPattern)) {
      trigger(`${name}.emailAddress`);
    }
  }, [emailTaken, emailWatch, name, trigger]);

  const fetchDietaryRestrictionsQuery = useFetchDietaryRestrictionsQuery();
  const fetchDietaryPreferencesQuery = useFetchDietaryPreferencesQuery();

  if (existingStaffMember && !getValues(name)) {
    return null;
  }

  const isExistingStaff = !!existingStaffMember?.emailAddress;
  const canEditEmail =
    user?.userType === UserTypeEnum.Admin ||
    (user?.userType === UserTypeEnum.StaffMember &&
      hasPermission(user as StaffMemberModel, StaffRoleEnum.SiteAdministrator, currentAccount?.id));

  return (
    <>
      <div className='grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6'>
        <Card shadow='sm' className='border border-gray-200'>
          <CardHeader className='flex gap-3'>
            <h3 className='text-lg font-semibold'>Staff Details</h3>
          </CardHeader>
          <Divider />
          <CardBody className='gap-4'>
            <Controller
              name={`${name}.firstName`}
              control={control}
              rules={{ required: 'This field is required' }}
              render={({ field, fieldState }) => (
                <Input
                  {...field}
                  label='First Name'
                  variant='bordered'
                  isRequired
                  isInvalid={fieldState.invalid}
                  errorMessage={fieldState.error?.message}
                />
              )}
            />
            <Controller
              name={`${name}.lastName`}
              control={control}
              rules={{ required: 'This field is required' }}
              render={({ field, fieldState }) => (
                <Input
                  {...field}
                  label='Last Name'
                  variant='bordered'
                  isRequired
                  isInvalid={fieldState.invalid}
                  errorMessage={fieldState.error?.message}
                />
              )}
            />
            <Controller
              name={`${name}.title`}
              control={control}
              render={({ field, fieldState }) => (
                <Input
                  {...field}
                  label='Title'
                  variant='bordered'
                  placeholder='Dr, Mr, Mrs, Ms...'
                  isInvalid={fieldState.invalid}
                  errorMessage={fieldState.error?.message}
                />
              )}
            />
          </CardBody>
        </Card>

        <Card shadow='sm' className='border border-gray-200'>
          <CardHeader className='flex gap-3'>
            <h3 className='text-lg font-semibold'>Contact Information</h3>
          </CardHeader>
          <Divider />
          <CardBody className='gap-4'>
            {isExistingStaff ? (
              <>
                <div className='flex flex-col gap-1'>
                  <span className='text-sm font-medium'>Email/Username *</span>
                  <div className='flex flex-row items-center gap-2'>
                    <Text>{existingStaffMember.emailAddress}</Text>
                    {canEditEmail && (
                      <Button variant='light' onClick={() => setEmailOpen(true)} isIconOnly>
                        <PencilSquareIcon title='Edit email' className='size-5' />
                      </Button>
                    )}
                  </div>
                </div>
                <input type='hidden' {...register(`${name}.emailAddress`)} />
              </>
            ) : (
              <Controller
                name={`${name}.emailAddress`}
                control={control}
                rules={{
                  required: 'This field is required',
                  pattern: {
                    value: emailPattern,
                    message: 'Invalid email',
                  },
                  validate: val => (val && emailTaken?.exists ? emailConflictMessage : true),
                }}
                render={({ field, fieldState }) => (
                  <Input
                    {...field}
                    type='email'
                    label='Email/Username'
                    variant='bordered'
                    isRequired
                    isInvalid={fieldState.invalid}
                    errorMessage={fieldState.error?.message}
                  />
                )}
              />
            )}
            {showExistingStaffLink && (
              <TextLink href={`/staff/edit/${emailTaken.userKey}`}>
                Open their profile to free this email
              </TextLink>
            )}
            <PhoneInput name={`${name}.cellPhone`} label='Cell Phone' control={control} />
            <PhoneInput name={`${name}.officePhone`} label='Office Phone' control={control} />
            <Controller
              name={`${name}.extension`}
              control={control}
              render={({ field, fieldState }) => (
                <Input
                  {...field}
                  label='Office Extension'
                  variant='bordered'
                  isInvalid={fieldState.invalid}
                  errorMessage={fieldState.error?.message}
                />
              )}
            />
            <Controller
              name={`${name}.notes`}
              control={control}
              render={({ field, fieldState }) => (
                <Textarea
                  {...field}
                  label='Internal Notes'
                  variant='bordered'
                  minRows={5}
                  isInvalid={fieldState.invalid}
                  errorMessage={fieldState.error?.message}
                />
              )}
            />
          </CardBody>
        </Card>

        <Card shadow='sm' className='border border-gray-200'>
          <CardHeader className='flex gap-3'>
            <h3 className='text-lg font-semibold'>Dietary Information</h3>
          </CardHeader>
          <Divider />
          <CardBody className='gap-6'>
            <Alert
              color='primary'
              title='The information in this section is visible to rep users.'
            />
            <DietarySelector
              otherName={`${name}.otherDietaryRestriction`}
              name={`${name}.dietaryRestrictions`}
              query={fetchDietaryRestrictionsQuery}
              label='Dietary Restrictions'
            />
            <DietarySelector
              query={fetchDietaryPreferencesQuery}
              otherName={`${name}.otherDietaryPreference`}
              name={`${name}.dietaryPreferences`}
              label='Dietary Preferences'
            />
            <Controller
              name={`${name}.foodNotes`}
              control={control}
              render={({ field, fieldState }) => (
                <Textarea
                  {...field}
                  label='Public Notes'
                  placeholder='Add any other notes you want available to rep users...'
                  variant='bordered'
                  minRows={5}
                  isInvalid={fieldState.invalid}
                  errorMessage={fieldState.error?.message}
                />
              )}
            />
          </CardBody>
        </Card>
        {children}
      </div>
      <EditEmailDialog isOpen={editEmailOpen} setIsOpen={setEmailOpen} user={existingStaffMember} />
    </>
  );
};

export default StaffFormFields;
